import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Avatar, Box, Card, IconButton, useMediaQuery, useTheme } from '@mui/material'
import React from 'react'

import ChatContainer from './ChatContainer'
import ChatList from './ChatList'

/**
 * ChatLayout - Responsive two-pane chat layout
 * Desktop: Shows chat list on left and conversation on right
 * Mobile: Shows one view at a time with back button
 *
 * @param {Array} conversations - Array of conversation objects
 * @param {string} selectedConversationId - ID of the selected conversation
 * @param {function} onSelectConversation - Callback when conversation is selected
 * @param {Array} messages - Messages for the selected conversation
 * @param {function} onSendMessage - Callback when message is sent
 * @param {string} currentUserId - ID of the current user
 * @param {Object} chatHeaderProps - Props for the chat header (title, subtitle, avatar, action)
 * @param {string} connectionState - WebSocket connection state
 * @param {boolean} fullScreen - Whether the chat is in full screen mode
 * @param {Object} sx - Additional styles to apply
 */
export default function ChatLayout({
  conversations = [],
  selectedConversationId,
  onSelectConversation,
  messages = [],
  onSendMessage,
  currentUserId,
  chatHeaderProps = {},
  connectionState,
  fullScreen = false,
  sx = {},
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const handleBack = () => {
    onSelectConversation(null)
  }

  // On mobile, show either list or conversation
  if (isMobile) {
    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: 'none',
          pt: 3,
          p: 0,
          ...sx,
        }}
      >
        {!selectedConversationId ? (
          <ChatList conversations={conversations} selectedConversationId={selectedConversationId} onSelectConversation={onSelectConversation} connectionState={connectionState} />
        ) : (
          <ChatContainer
            messages={messages}
            onSendMessage={onSendMessage}
            currentUserId={currentUserId}
            {...chatHeaderProps}
            fullScreen={fullScreen}
            connectionState={connectionState}
            backButton={
              <IconButton onClick={handleBack} edge="start" sx={{ border: 'none', backgroundColor: 'transparent' }}>
                <ArrowBackIcon />
              </IconButton>
            }
            sx={{
              p: 0,
              m: 0,
            }}
          />
        )}
      </Card>
    )
  }

  // Desktop: Two-pane layout
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        overflow: 'hidden',
        ...sx,
      }}
    >
      {/* Left pane: Chat list */}
      <Box
        sx={{
          width: 360,
          flexShrink: 0,
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ChatList conversations={conversations} selectedConversationId={selectedConversationId} onSelectConversation={onSelectConversation} connectionState={connectionState} />
      </Box>

      {/* Right pane: Conversation */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        {selectedConversationId ? (
          <ChatContainer
            messages={messages}
            onSendMessage={onSendMessage}
            currentUserId={currentUserId}
            {...chatHeaderProps}
            fullScreen={fullScreen}
            connectionState={connectionState}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              bgcolor: 'background.default',
            }}
          >
            <Box
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  margin: '0 auto',
                  mb: 2,
                  bgcolor: 'action.selected',
                }}
              >
                💬
              </Avatar>
              <Box sx={{ fontSize: '1.2rem', fontWeight: 500 }}>Select a conversation</Box>
              <Box sx={{ fontSize: '0.9rem', mt: 1 }}>Choose a conversation from the list to start messaging</Box>
            </Box>
          </Box>
        )}
      </Box>
    </Card>
  )
}
